import { MotorsDraft, SaveDraft, ValidationResult } from '@classifieds-sell/datalayer';
import {
    MotorsDraftFacade,
    MotorsDraftState,
    IRouterData,
    RouterFacade
} from '@classifieds-sell/state';
import { isStepEnabled } from '@classifieds-sell/shared';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    getMotorsNameByStepIdAndCategory,
    LAST_SAVED_STEP_ID_PROPERTY_NAME,
    SUB_CATEGORY_PROPERTY_NAME
} from '../models/draft/draft.constants';
import { StepIds } from '../models/motors-step/motors-step-model';
import { MotorsSectionBase } from '../sections/motors-section-base';
import { getDraftStorageKey, getTimestampStorageKey } from 'libs/datalayer/src/lib/apimodels/draft/draft-utils';

export abstract class MotorsPageBase {
    private _sections: Array<MotorsSectionBase> = [];
    _destroyed$: Subject<void> = new Subject<void>();

    hasUpdatedCurrentStep = false;
    hasRunInitialValidation = false;
    hasRunDraftDataValidation = false;
    draftState: MotorsDraftState;
    routerData: IRouterData;

    constructor(
        private readonly stepId: StepIds,
        public draftFacade: MotorsDraftFacade,
        public routerFacade: RouterFacade
    ) { }

    onInit(): void {
        this.getData();
    }

    onDestroy(): void {
        if (isStepEnabled(this.routerData.steps, this.stepId)) {
            const validation: ValidationResult = {
                stepId: this.stepId,
                isValid: this.isValid()
            };

            // Leaving the current step - update the step validation
            this.draftFacade.updateValidationResults(validation);
        }

        this._destroyed$.next();
    }

    registerSection(section: MotorsSectionBase): void {
        if (section && !this.hasSection(section)) {
            this._sections.push(section);
        }
    }

    unregisterSection(section: MotorsSectionBase): void {
        const index = this._sections.indexOf(section);
        if (index > -1) {
            this._sections.splice(index, 1);
        }
    }

    hasSection(section: MotorsSectionBase): boolean {
        return this._sections.indexOf(section) > -1;
    }

    public isValid = (): boolean => {
        let valid = true;
        // need to invoke all sections' isValid func
        this._sections.forEach((s) => {
            if (!s.isValid()) {
                valid = false;
            }
        });
        return valid;
    };

    public dirty = (): boolean => {
        return this._sections.some((s) => s.dirty());
    };

    getData(): void {
        combineLatest([
            this.routerFacade.routerData$,
            this.draftFacade.state$,
            this.routerFacade.isEditOrRelistPage$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                ([routerStateData, draftStateData, isEditOrRelistPage]) => {
                    this.routerData = { ...routerStateData };
                    this.draftState = { ...draftStateData };

                    if (!this.hasUpdatedCurrentStep) {
                        this.hasUpdatedCurrentStep = true;
                        if (!isEditOrRelistPage) {
                            this.updateCurrentStep();
                        }
                    }

                    if (this.draftState.validationResults) {
                        const isCurrentStepInvalid =
                            this.draftState.validationResults.some(
                                (s) =>
                                    s.stepId === this.stepId &&
                                    s.isValid === false
                            );

                        // this is to show the validation error if any on the step
                        // as soon as user' landed on the step
                        if (
                            isCurrentStepInvalid &&
                            !this.hasRunInitialValidation
                        ) {
                            // validation should run after view has initiated
                            // not using delayService here reason being
                            // to reduce the number of dependencies on child components
                            setTimeout(() => {
                                this.isValid();
                            }, 0);
                            this.hasRunInitialValidation = true;
                        }

                        if (!this.hasRunDraftDataValidation) {
                            this.hasRunDraftDataValidation = true;
                            this.validateDraftData();
                        }
                    }
                }
            );
    }

    public saveDraft = (listingId?: number): void => {
        const propName = getMotorsNameByStepIdAndCategory(
            <StepIds>this.routerData.stepInfo.stepId,
            this.routerData.categoryId
        );
        const draft: { [index: string]: any } = {};
        draft[LAST_SAVED_STEP_ID_PROPERTY_NAME] =
            this.routerData.stepInfo.stepId;
        draft[propName] = {
            ...this.draftState.draftData[propName]
        };
        draft[SUB_CATEGORY_PROPERTY_NAME] = this.routerData.subCategory;

        if(listingId) {
            this.saveEditListingAsDraft(listingId);
            return;
        }

        const draftToSave = {
            categoryId: this.routerData.categoryId,
            subCategory: this.routerData.subCategory,
            draftData: JSON.stringify(draft)
        } as SaveDraft;

        if (this.draftState.id > 0) {
            draftToSave.draftId = this.draftState.id;
        }

        this.draftFacade.saveDraft(draftToSave);
    };

    private saveEditListingAsDraft = (listingId?: number): void => {
            if (!listingId) {
                return;
            }
            const draftToSave: MotorsDraft = {
                id: this.draftState.id > 0 ? this.draftState.id : listingId,
                categoryId: this.routerData.categoryId,
                status: this.draftState.status ?? 0,
                draftData: this.draftState.draftData,
                validationSchema: this.draftState.validationSchema ?? {},
                metadata: this.draftState.metadata,
                validationResults: this.draftState.validationResults ?? [],
                originalDraftData: this.draftState.originalDraftData
            };

            const localStorageKey = getDraftStorageKey(
                draftToSave.categoryId,
                listingId,
                this.routerData.subCategory
            );
            const localTimestampKey = getTimestampStorageKey(
                draftToSave.categoryId,
                listingId,
                this.routerData.subCategory
            );
            localStorage.setItem(localStorageKey, JSON.stringify(draftToSave));
            localStorage.setItem(localTimestampKey, Date.now().toString());
        };

    private updateCurrentStep(): void {
        this.draftFacade.updateCurrentStep({
            categoryId: this.routerData.categoryId,
            currentStepId: this.routerData.stepInfo.stepId
        });
    }

    private validateDraftData(): void {
        // on summary page need to validate the number of photos against packages again.
        if (this.stepId === StepIds.summary &&
            !this.routerData.agent &&
            !this.validMinPhotoRequirement()) {
            const validation: ValidationResult = {
                stepId: StepIds.descriptionAndPhotos,
                isValid: false
            };

            this.draftFacade.updateValidationResults(validation);
        }
    }

    private validMinPhotoRequirement(): boolean {
        const selectedPackage = this.draftState.metadata.fees.find((i) =>
            i.id === this.draftState.draftData.listingDetails.categoryPackageId &&
            i.sellingType === this.draftState.draftData.listingDetails.sellingType);

        const totalPhotos =
            this.draftState.draftData?.descriptionAndPhotos?.photos === null ? 0 :
            this.draftState.draftData?.descriptionAndPhotos?.photos.length;

        if (
            selectedPackage.minPhotoRequired === 0 ||
            selectedPackage.minPhotoRequired === null ||
            selectedPackage.minPhotoRequired <= totalPhotos
        ) {
            return true;
        } else {
            return false;
        }
    }
}
